// src/services/auth.js
import axios from 'axios';

const API_URL = '/api';

const auth = {
    login: async (username, password) => {
        try {
            const response = await axios.post(`${API_URL}/auth/login`, {
                username,
                password
            });

            const { token } = response.data;

            // Use sessionStorage instead of localStorage
            sessionStorage.setItem('authToken', `Bearer ${token}`);
            sessionStorage.setItem('isAdmin', 'true');

            return response.data;
        } catch (error) {
            console.error('Login error:', error);
            throw error;
        }
    },

    logout: () => {
        // Clear session data
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('isAdmin');
        window.location.href = '/';
    },

    // Get the current auth token
    getToken: () => {
        return sessionStorage.getItem('authToken');
    },

    // Check if user is authenticated
    isAuthenticated: () => {
        const token = sessionStorage.getItem('authToken');
        return !!token;
    },

    // Check if user is admin
    isAdmin: () => {
        return sessionStorage.getItem('isAdmin') === 'true';
    },

    // Refresh token
    refreshToken: async () => {
        try {
            const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
                headers: {
                    Authorization: auth.getToken()
                },
                skipAuthRefresh: true // Flag to skip interceptor
            });

            const { token } = response.data;
            sessionStorage.setItem('authToken', `Bearer ${token}`);

            return token;
        } catch (error) {
            console.error('Token refresh error:', error);
            auth.logout();
            throw error;
        }
    },

    // Initialize auth state
    init: () => {
        // Clean up old localStorage tokens (migration to sessionStorage)
        localStorage.removeItem('token');
        localStorage.removeItem('authToken');
        localStorage.removeItem('isAdmin');

        // Add request interceptor for automatic token refresh
        axios.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Prevent infinite loop: don't retry if already retried or if it's the refresh endpoint
                if (error.response?.status === 401 &&
                    !originalRequest._retry &&
                    !originalRequest.skipAuthRefresh) {

                    originalRequest._retry = true;

                    try {
                        await auth.refreshToken();
                        // Retry the original request
                        originalRequest.headers.Authorization = auth.getToken();
                        return axios(originalRequest);
                    } catch (refreshError) {
                        return Promise.reject(refreshError);
                    }
                }
                return Promise.reject(error);
            }
        );

        // Add request interceptor to add auth header
        axios.interceptors.request.use(
            (config) => {
                const token = auth.getToken();
                if (token) {
                    config.headers.Authorization = token;
                }
                return config;
            },
            (error) => {
                return Promise.reject(error);
            }
        );
    }
};

// Initialize auth when imported
auth.init();

export default auth;